import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/Input';
import Button from '../components/Button';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (imageRef.current?.complete) {
      setImageLoaded(true);
    }
  }, []);

  function validate() {
    const errors = {};
    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address.';
    }
    if (!password) {
      errors.password = 'Password is required.';
    }
    return errors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(email.trim(), password);
      if (result.success) {
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.message);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className={`login-left${imageLoaded ? ' is-loaded' : ''}`}>
        {!imageLoaded && <div className="login-left-shimmer" aria-hidden="true" />}
        <img
          ref={imageRef}
          src={`${process.env.PUBLIC_URL}/images/login-left.png`}
          alt="login"
          onLoad={() => setImageLoaded(true)}
        />
      </div>
      <form className="login-form" onSubmit={handleSubmit}>
        <div className='login-logo'>
          <img src={`${process.env.PUBLIC_URL}/images/login-logo.svg`} alt="logo"/>
        </div>
        <h2 className="login-title">Login </h2>
        <p className="login-subtitle">Please enter your admin credentials below.</p>

        {error && <div className="login-error">{error}</div>}

        <Input
          id="email"
          label="Email"
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
          onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
          placeholder="Enter your email"
          error={fieldErrors.email}
        />
        <Input
          id="password"
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          error={fieldErrors.password}
        />
        <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>

        <Button type="submit" variant="primary" className="login-btn" disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Login'}
        </Button>
      </form>
    </div>
  );
}
